import { CommonModule } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MapModalComponent } from 'app/shared/components/map-modal/map-modal.component';
import {
  MapModalTypeEnum,
  MonitorDocumentTypeEnum,
} from 'app/shared/components/map-modal/models/map-modal.enum';
import { MapModalInputData } from 'app/shared/components/map-modal/models/map-modal.interface';

/**
 * Reusable geolocation button that opens the shared map modal (MapModalComponent,
 * SIMPLE mode) for a single customer address.
 *
 * Works anywhere a customer address is shown: it accepts the raw `geolocationPoint`
 * ([latitude, longitude]) plus the customer identifiers, and always opens the same
 * modal. The icon turns "warn" when the point is missing or invalid ([0, 0]).
 */
@Component({
  selector: 'app-address-map-button',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    <mat-icon
      [color]="hasValidGeolocation() ? 'primary' : 'warn'"
      [matTooltip]="hasValidGeolocation() ? tooltip() : noLocationTooltip()"
      style="cursor: pointer"
      (click)="openMap(); $event.stopPropagation()"
    >
      location_on
    </mat-icon>
  `,
})
export class AddressMapButtonComponent {
  private readonly dialog = inject(MatDialog);

  /** Address coordinates as [latitude, longitude]. */
  readonly geolocationPoint = input<number[] | null | undefined>();
  /** Customer Mongo ObjectId (_id). */
  readonly customerMongoId = input<string | null | undefined>();
  /** Customer numeric id. */
  readonly customerId = input<number | null | undefined>();
  /** Customer display name, used as the marker title. */
  readonly customerName = input<string | null | undefined>('');
  /** Optional address label shown on the marker. */
  readonly addressName = input<string | null | undefined>();
  readonly tooltip = input<string>('Ver ubicación');
  readonly noLocationTooltip = input<string>('Sin geolocalización');

  readonly hasValidGeolocation = computed(() => {
    const point = this.geolocationPoint();
    return (
      Array.isArray(point) &&
      point.length >= 2 &&
      !(Number(point[0]) === 0 && Number(point[1]) === 0)
    );
  });

  openMap(): void {
    if (!this.hasValidGeolocation()) {
      return;
    }

    const geolocationPoint = this.geolocationPoint() ?? [];
    const addressName = this.addressName() ?? undefined;

    // Shape understood by buildMarkerSimple: it reads
    // `addresses[0].geolocationPoint || principalAddress.geolocationPoint`.
    const element = {
      _id: this.customerMongoId(),
      id: this.customerId(),
      name: this.customerName() ?? '',
      principalAddress: { name: addressName, geolocationPoint },
      addresses: [{ name: addressName, geolocationPoint }],
    };

    const data: MapModalInputData = {
      type: MapModalTypeEnum.SIMPLE,
      tripId: null,
      element,
      document: MonitorDocumentTypeEnum.NONE,
      groupDocuments: [],
      map: {
        displayMode: 'drawer',
        captions: { title: 'Ubicación' },
      },
    };

    this.dialog.open(MapModalComponent, {
      panelClass: ['responsive-modal'],
      width: '90%',
      data,
    });
  }
}
